import React from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { ICustomerSatisfactionWidgetData } from "@ts/common-types";
import { titleCase } from "@ts/utils";
import NoData from 'highcharts/modules/no-data-to-display';
import WidgetCard from "./WidgetCard";

NoData(Highcharts);

interface ICustomerSatisfactionDriversWidget {
    title?: string;
    type?: string;
    widgetData: ICustomerSatisfactionWidgetData;
}

const CustomerSatisfactionDriversWidget: React.FC<ICustomerSatisfactionDriversWidget> = ({ title, type, widgetData }) => {
    const chartRefs = React.useRef([]);

    const getChartOptions = (
        categories: Array<string>,
        series: Array<{
            name?: string;
            data?: number[];
            color?: string;
        }>
    ) => ({
        chart: {
            type: "bar",
            responsive: true,
            events: {
                load: function(this: any) {
                    const chart = this;
                    const legendGroup = chart.legend.group.element;

                    const leftText = document.createElement('div');
                    leftText.innerHTML = '# Mentions in Reviews';
                    leftText.style.position = 'absolute';
                    leftText.style.fontSize = '12px';
                    leftText.style.left = '10px';
                    leftText.style.top = (legendGroup.getBBox().y) + 'px';

                    const rightText = document.createElement('div');
                    rightText.innerHTML = 'NPS Score';
                    rightText.style.position = 'absolute';
                    rightText.style.fontSize = '12px';
                    rightText.style.right = '20px';
                    rightText.style.top = (legendGroup.getBBox().y) + 'px';

                    chart.container.appendChild(leftText);
                    chart.container.appendChild(rightText);
                }
            }
        },
        title: {
            text: "",
            align: "left",
        },
        xAxis: [
            {
                categories: categories,
                title: {
                    text: "",
                },
                lineWidth: 0,
                gridLineWidth: 0,
                labels: {
                    formatter: function(this: any) {
                        return titleCase(this.value.replace(/_/g, " "));
                    },
                },
            },
            {
                title: {
                    text: "",
                },
                categories: widgetData?.npsData ? widgetData?.npsData.map((nps: number) => nps) : [],
                linkedTo: 0,
                opposite: true,
                lineWidth: 0,
                gridLineWidth: 0,
                labels: {
                    style: {
                        fontWeight: "bolder",
                        color: "#000",
                        fontSize: "12px"
                    }
                }
            },
        ],
        yAxis: {
            min: 0,
            title: {
                text: "Count",
            },
            visible: false,
            gridLineWidth: 0
        },
        legend: {
            layout: 'horizontal',
            align: 'center',
            floating: false,
            verticalAlign: 'top',
            symbolRadius: 0,
            symbolWidth: 10,
            itemStyle: {
                fontSize: '10px'
            }
        },
        credits: {
            enabled: false,
        },
        plotOptions: {
            series: {
                stacking: "normal"
            },
        },
        series,
        lang: {
            noData: "No data to display"
        },
        noData: {
            style: {
                fontWeight: 'bold',
                fontSize: '15px',
                color: '#303030'
            }
        },
        responsive: {
            rules: [
                {
                    condition: {
                        maxWidth: 400,
                    },
                    chartOptions: {
                        legend: {
                            align: "center",
                            verticalAlign: "bottom",
                            layout: "horizontal",
                        },
                    },
                },
            ],
        },
    });

    return (
        <WidgetCard
        className={type === "sub-module" ? "bg-transparent border-0 shadow-none" : ""}
    >
            {type !== "sub-module" ? (
                <h5 className="mb-3">
                    {title || "Customer Satisfaction Drivers*"}
                </h5>
            ) : null}
            <HighchartsReact
                highcharts={Highcharts}
                options={getChartOptions(
                    widgetData?.chartDataSeries?.categories || [],
                    widgetData?.chartDataSeries?.series || []
                )}
                ref={(el) => (chartRefs.current[1] = el)}
            />
        </WidgetCard>
    );
};

export default CustomerSatisfactionDriversWidget;